import { useOktaAuth } from "@okta/okta-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { env } from "@/env";

interface LocationStateWithFrom {
  from?: { pathname?: string };
}

/**
 * Login page — kicks off the Okta OIDC flow in production profiles.
 *
 * In local profile the AuthProvider bypasses Okta entirely so this page
 * just redirects to /chains.
 *
 * Earlier this component navigated to `/chains` on button-click and
 * expected `RequireAuth` to trigger Okta. That didn't work: `RequireAuth`
 * just navigates BACK to /login when unauthenticated, with no call to
 * `signInWithRedirect`. The result was an invisible /login ↔ /chains
 * bounce that looked like the button did nothing. Fix: call
 * `signInWithRedirect` directly from the click handler.
 */
export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  // Local-dev bypass: no Okta, just go straight to /chains.
  useEffect(() => {
    if (!env.oktaEnabled) {
      navigate("/chains", { replace: true });
    }
  }, [navigate]);

  if (!env.oktaEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500 text-sm">Redirecting…</div>
      </div>
    );
  }

  return <OktaSignInButton location={location} />;
}

interface OktaSignInButtonProps {
  location: ReturnType<typeof useLocation>;
}

function OktaSignInButton({ location }: OktaSignInButtonProps): JSX.Element {
  const { oktaAuth, authState } = useOktaAuth();
  const [signingIn, setSigningIn] = useState(false);

  // Where to send the user after a successful Okta round-trip. Prefer
  // the `from` location that `RequireAuth` stashed when it bounced
  // them here; fall back to `/chains` (the canonical landing page).
  const fromState = location.state as LocationStateWithFrom | null;
  const originalUri = fromState?.from?.pathname ?? "/chains";

  // If the user lands on /login but is ALREADY authenticated (e.g.,
  // bookmark with valid session), skip the button entirely and go
  // straight to the originalUri.
  const navigate = useNavigate();
  useEffect(() => {
    if (authState?.isAuthenticated) {
      navigate(originalUri, { replace: true });
    }
  }, [authState?.isAuthenticated, navigate, originalUri]);

  const handleSignIn = async (): Promise<void> => {
    if (signingIn) return;
    setSigningIn(true);
    try {
      await oktaAuth.signInWithRedirect({ originalUri });
    } catch (err) {
      setSigningIn(false);
      // Surface the error to the console; UI stays on /login so user
      // can retry. Common causes: Okta config mismatch, network error,
      // PKCE state collision.
      console.error("Okta signInWithRedirect failed", err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="card max-w-md w-full p-8 text-center space-y-4">
        <h1 className="text-2xl font-semibold text-slate-900">truck-tour-finder</h1>
        <p className="text-sm text-slate-600">
          Sign in with your Echo Okta account to continue.
        </p>
        <button
          type="button"
          className="btn-primary w-full"
          onClick={() => {
            void handleSignIn();
          }}
          disabled={signingIn}
        >
          {signingIn ? "Redirecting to Okta…" : "Sign in with Okta"}
        </button>
      </div>
    </div>
  );
}
